import { useCallback, useMemo } from "react";

import { APIContentComponent, APIVendorComponent } from "@companion-core/shared/app/Interfaces/API";
import { trackBookingCTA } from "@companion-core/shared/app/tracking/contentDetails";
import {
  getDynamicComponentId,
  getDynamicComponentVendorId,
} from "@companion-core/shared/app/Utils/tracking";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { Category } from "@companion-core/shared/app/Components/Bell/Categories";
import { getCategoryNameFromFamily } from "@companion-core/shared/app/Utils/contentHelper";
import {
  BellEntryResolution,
  BellFailureReason,
  DEFAULT_BELL_CATEGORY,
  reportBellEntryFailure,
} from "@companion-core/shared/app/Utils/bell";
import { BellReferrer, useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { useWizardStore } from "@companion-core/shared/app/Store/wizardStore";
import { getContentComponentMediaUrl } from "@companion-core/shared/app/Utils/componentHelper";
import { useDetailsPage } from "@companion-core/shared/app/Components/DetailsPages/hooks/detailsPage";
import { tryLoadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";
import { usePersonalizeField } from "@companion-core/shared/app/Hooks/personalization";

const BELL_REFERRER_CONTENT_TYPES = ["vendor", "offer"];

const resolveLoadableForm = (
  candidate?: Category
): { category: Category; formPath: string; degraded: boolean } | null => {
  if (!candidate) return null;
  if (candidate.offerFormPath && tryLoadFormFromPath(candidate.offerFormPath)) {
    return { category: candidate, formPath: candidate.offerFormPath, degraded: false };
  }
  if (candidate.formPath && tryLoadFormFromPath(candidate.formPath)) {
    return {
      category: candidate,
      formPath: candidate.formPath,
      degraded: Boolean(candidate.offerFormPath),
    };
  }
  return null;
};

export const useContentTemplate = (
  data: APIContentComponent & APIVendorComponent,
  fromOnboarding?: boolean,
  isMobileSize?: boolean
) => {
  const { t, i18n, handleScroll, lang } = useDetailsPage();
  const { personalizeField } = usePersonalizeField();
  const {
    setBellContent,
    setBellCategory,
    setFormPath,
    setVendorApiValues,
    clearBell,
    setBellReferrer,
    setFromOnboarding,
  } = useBellStore();
  const { setCurrentForm } = useWizardStore();
  const forms = useConfigStore((state) => state.forms);

  const family = data?.vendor?.family ?? data?.family ?? "";
  const subfamily = data?.vendor?.subfamily ?? data?.subfamily ?? undefined;

  const categoryName = data ? getCategoryNameFromFamily({ family, subfamily }) : undefined;

  const bellResolution: BellEntryResolution = useMemo(() => {
    const bellModule = getModule("concierge")?.modules?.bell;
    if (!bellModule || bellModule.enabled === false) {
      return { outcome: "unavailable", failureReason: "bellUnavailable" };
    }

    const categories: Category[] = bellModule.options?.categories ?? [];
    const defaultCategory: Category | undefined = bellModule.options?.defaultCategory ?? undefined;
    const category = categoryName
      ? categories.find((candidate: Category) => candidate.name === categoryName)
      : undefined;

    const failureReason: BellFailureReason = !categoryName
      ? "unknownFamily"
      : category
        ? "unloadableForm"
        : "missingCategory";

    const resolved = resolveLoadableForm(category);
    if (resolved) {
      return {
        outcome: "form",
        category: resolved.category,
        formPath: resolved.formPath,
        ...(resolved.degraded ? { failureReason: "unloadableForm" as BellFailureReason } : {}),
      };
    }

    const fallback =
      resolveLoadableForm(defaultCategory) ?? resolveLoadableForm(DEFAULT_BELL_CATEGORY);
    if (fallback) {
      return {
        outcome: "form",
        category: fallback.category,
        formPath: fallback.formPath,
        failureReason,
      };
    }

    return { outcome: "categories", failureReason };
  }, [data, categoryName, forms]);

  const bellReferrer: BellReferrer | undefined = BELL_REFERRER_CONTENT_TYPES.includes(
    data?.content_ct ?? ""
  )
    ? (data.content_ct as BellReferrer)
    : undefined;

  const reportBellFailure = useCallback(() => {
    if (!bellResolution.failureReason) return;
    reportBellEntryFailure({
      reason: bellResolution.failureReason,
      outcome: bellResolution.outcome,
      family: family || undefined,
      subfamily,
      categoryName,
      formPath: bellResolution.category?.formPath,
      offerFormPath: bellResolution.category?.offerFormPath,
      contentId: getDynamicComponentId(data) || undefined,
      contentType: data?.content_ct,
    });
  }, [bellResolution, family, subfamily, categoryName, data]);

  const onBookingCTAVisited = useCallback(
    (offerFunnelStepNumber = "0") => {
      if (data) {
        trackBookingCTA({
          fulfillmentMode: data.booking_process?.type as string | undefined,
          vendorId: getDynamicComponentVendorId(data),
          contentId: getDynamicComponentId(data),
          offerFunnelStepNumber,
        });
      }
    },
    [data]
  );

  const bannerImageUrl: string | undefined = useMemo(() => {
    if (!data) return undefined;
    const mediaTypes = isMobileSize
      ? (["detail_mobile", "detail", "image"] as const)
      : (["detail", "image"] as const);
    return mediaTypes.reduce<string | undefined>(
      (url, type) => url ?? getContentComponentMediaUrl(data, type),
      undefined
    );
  }, [data, isMobileSize]);

  const contentTitle = useMemo(() => personalizeField(data?.title) ?? "", [data, personalizeField]);
  const contentSubtitle = useMemo(() => personalizeField(data?.subtitle), [data, personalizeField]);

  const contentDescription = useMemo(
    () => personalizeField(data?.description),
    [data, personalizeField]
  );
  const contentConditions = useMemo(
    () => personalizeField(data?.booking_process?.conditions),
    [data, personalizeField]
  );
  const contentLegalNotice = useMemo(
    () => personalizeField(data?.legal_notice),
    [data, personalizeField]
  );

  const googlePlaceData = useMemo(() => {
    return data?.vendor?.google_place ?? data?.google_place;
  }, [data]);

  const contentTags = useMemo(() => {
    return data?.tags_v2;
  }, [data]);

  const initDegradedBell = useCallback(() => {
    clearBell();
    setBellContent("categories", false, bellReferrer);
  }, [clearBell, setBellContent, bellReferrer]);

  const initBellFromDynamicPage = useCallback((): "form" | "categories" => {
    const jsonForm = tryLoadFormFromPath(bellResolution.formPath);
    if (!jsonForm || !bellResolution.category || !bellResolution.formPath) {
      reportBellEntryFailure({
        reason: "unloadableForm",
        outcome: "categories",
        family: family || undefined,
        subfamily,
        categoryName,
        formPath: bellResolution.formPath,
        offerFormPath: bellResolution.category?.offerFormPath,
        contentId: getDynamicComponentId(data) || undefined,
        contentType: data?.content_ct,
      });
      return "categories";
    }

    clearBell();
    if (bellReferrer) setBellReferrer(bellReferrer);
    const vendorApiValues = {
      subject: data?.vendor?.name ?? data?.name,
      vendor: {
        // eslint-disable-next-line camelcase
        offer_id: data.content_ct === "offer" ? data?.parent_id : null,
        // eslint-disable-next-line camelcase
        vendor_id: data?.vendor?.id ?? data?.parent_id,
      },
      // eslint-disable-next-line camelcase
      start_location: googlePlaceData?.address,
      family: data?.vendor?.family ?? data?.family,
      subfamily: data?.vendor?.subfamily ?? data?.subfamily,
    };
    setFromOnboarding(fromOnboarding ?? false);
    setBellContent("form", false);
    setBellCategory(bellResolution.category);
    setFormPath(bellResolution.formPath);
    setVendorApiValues(vendorApiValues);
    setCurrentForm(jsonForm);
    return "form";
  }, [data, bellResolution, bellReferrer, fromOnboarding, googlePlaceData]);

  return {
    googlePlaceData,
    t,
    i18n,
    onBookingCTAVisited,
    bellResolution,
    reportBellFailure,
    initBellFromDynamicPage,
    initDegradedBell,
    bannerImageUrl,
    lang,
    handleScroll,
    contentTitle,
    contentSubtitle,
    contentDescription,
    contentConditions,
    contentLegalNotice,
    contentTags,
  };
};
